import { useEffect, useState } from 'react';

import { SyncOutlined } from '@ant-design/icons';
import { RiArrowRightUpLine } from '@remixicon/react';
import {
  getOrganizationLogo,
  ORGANIZATION_CODES,
  OrganizationCodes,
  getBankName,
} from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { AntColumnType } from '@revenuemarket/ui/antd/Table';
import { Button, Image, Table, Typography } from 'antd';

import { BankType, WiringAccountListItem } from '~apis/wiring';
import { useGetWiringAccountList } from '~hooks/queries/wiring/query';
import { useCompanyId } from '~hooks/useCompanyId';
import useWiringCheck from '~hooks/useWiringCheck';
import { getAccountTypeText } from '~models/bank/enum';

import WiringTypeSelector from './WiringTypeSelector';
import { CompressInfo, ReservationInfo, WiringDepositInfoModel, WiringSelectModel } from './types';

const HANA_BANK_CODE = '081' as const;
const KB_BANK_CODE = '004' as const;

const ATTACHMENT_LINK =
  'https://partners-web-static.s3.ap-northeast-2.amazonaws.com/clobe/kb_biz_openAPI_guide.pdf';

const COLUMNS: AntColumnType<WiringAccountListItem> = [
  {
    title: '은행',
    dataIndex: 'bankCode',
    key: 'bankCode',
    render: (organizationCode: OrganizationCodes) => (
      <Flex gap={10} align="center">
        <Image
          preview={false}
          width={16}
          height={16}
          src={getOrganizationLogo(organizationCode).x2}
          alt={ORGANIZATION_CODES[organizationCode]}
        />
        <Typography>{getBankName(organizationCode)}</Typography>
      </Flex>
    ),
  },
  {
    title: '계좌번호',
    dataIndex: 'displayAccountNumber',
    key: 'displayAccountNumber',
  },
  {
    title: '구분',
    dataIndex: 'accountType',
    key: 'accountType',
    render: (accountType) => <Typography>{getAccountTypeText(accountType)}</Typography>,
  },
  {
    title: '계좌별칭',
    dataIndex: 'aliasName',
    key: 'aliasName',
  },
  {
    title: '현재 잔액',
    dataIndex: 'accountBalance',
    key: 'accountBalance',
    align: 'right',
    render: (balance) => <Typography>{(balance ?? 0).toLocaleString() + '원'}</Typography>,
  },
];

interface Props {
  model: WiringSelectModel;
  onFinish: (model: WiringDepositInfoModel) => void;
  payrollMode: boolean;
  defaultWithdrawalAccountId?: number;
  defaultScheduledInfo?: ReservationInfo;
  defaultCompressInfo?: CompressInfo;
}

const WithdrawalSelectStep: React.FC<Props> = ({ ...props }) => {
  const { companyId } = useCompanyId();

  const [reservationInfo, setReservationInfo] = useState<ReservationInfo>(
    props?.defaultScheduledInfo ?? {},
  );

  // 압축기장여부
  const [compressInfo, setCompressInfo] = useState<CompressInfo>(props?.defaultCompressInfo ?? {});

  const [selectedBank, setSelectedBank] = useState<BankType>(props?.model?.wiringBank ?? 'HANA');
  const { check } = useWiringCheck();

  const { data: accountListData, refetch, isFetching } = useGetWiringAccountList({ companyId });
  const realAccountList = accountListData?.Data?.data?.items || [];
  const test1AccountList = [
    {
      accountId: 20,
      bankCode: '004',
      accountNumber: '65810101610578',
      displayAccountNumber: '6581010161057',
      accountType: 'CHECKING',
      aliasName: '별칭',
      accountBalance: 999999999,
      isOverDraft: false,
    } as WiringAccountListItem,
  ];

  const test2AccountList = [
    {
      accountId: 20,
      bankCode: '004',
      accountNumber: '067010510870',
      displayAccountNumber: '067010510870',
      accountType: 'CHECKING',
      aliasName: '별칭',
      accountBalance: 999999999,
      isOverDraft: false,
    } as WiringAccountListItem,
  ];

  const accountList =
    companyId === '2u0PJ5wMuzwTDSM9uftqH' || companyId === '2wYLConD3c0iqNtDyGlV7'
      ? test1AccountList
      : companyId === '2vJWdCS1iUIB5edMUvPsp'
        ? test2AccountList
        : realAccountList;

  const [isPayrollMode, setIsPayrollMode] = useState(props.model?.isPayroll || props.payrollMode);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    props?.model?.accountInfoId || null,
  );

  const selectedAccountNumber = accountList.find((item) => item.accountId === selectedAccountId)
    ?.accountNumber;

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: WiringAccountListItem[]) => {
      const selectedRow = selectedRows[0];
      if (!selectedRow) {
        setSelectedAccountId(null);
        return;
      }
      const selectedBank =
        selectedRow.bankCode === HANA_BANK_CODE
          ? 'HANA'
          : selectedRow.bankCode === KB_BANK_CODE
            ? 'KB'
            : 'HANA';
      setSelectedBank(selectedBank);
      setSelectedAccountId(selectedRow.accountId);
    },
  };

  const buttonDisabled =
    !selectedAccountId ||
    (reservationInfo?.isReservation
      ? !reservationInfo?.reservationDate || !reservationInfo?.reservationTime
      : false);

  const handleClickKBGuide = async () => {
    try {
      const response = await fetch(ATTACHMENT_LINK);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '기업_OpenAPI_서비스_guide.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
    }
  };

  useEffect(() => {
    if (props.defaultWithdrawalAccountId) {
      const account = accountList.find(
        (item) => item.accountId === props.defaultWithdrawalAccountId,
      );
      if ((account?.accountBalance ?? 0) > 0) {
        setSelectedBank(account?.bankCode === HANA_BANK_CODE ? 'HANA' : 'KB');
        setSelectedAccountId(props.defaultWithdrawalAccountId);
      }
    }
  }, [props.defaultWithdrawalAccountId, accountList]);

  useEffect(() => {
    if (accountList?.length === 1) {
      const account = accountList[0];
      const bankType =
        account.bankCode === HANA_BANK_CODE
          ? 'HANA'
          : account.bankCode === KB_BANK_CODE
            ? 'KB'
            : 'HANA';
      setSelectedBank(bankType);
      setSelectedAccountId(account.accountId);
    }
  }, [accountList]);

  return (
    <Flex direction="column" gap={24}>
      <Flex direction="column" gap={4}>
        <Flex
          style={{
            padding: '10px 16px',
            background: colorTokens.bgPrimary,
          }}
          gap={8}
          direction="column"
          justify="center"
        >
          <Flex gap={8} align="center">
            {[KB_BANK_CODE, HANA_BANK_CODE].map((bankCode) => (
              <Image
                key={bankCode}
                width={20}
                height={20}
                src={getOrganizationLogo(bankCode).x2}
                alt={bankCode}
              />
            ))}
            <Typography>지금은 국민, 하나은행 계좌에서만 이체할 수 있어요.</Typography>
            <Button
              type="link"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              onClick={() => {
                window.open('https://biz.kebhana.com/index.jsp', '_blank');
              }}
            >
              하나은행 기업뱅킹 바로가기
              <RiArrowRightUpLine size="16" color={colorTokens.iconPrimary} />
            </Button>
            <Button
              type="link"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              onClick={() => {
                window.open('https://obiz.kbstar.com/quics?page=obiz', '_blank');
              }}
            >
              국민은행 기업뱅킹 바로가기
              <RiArrowRightUpLine size="16" color={colorTokens.iconPrimary} />
            </Button>
          </Flex>
          {selectedBank === 'KB' && (
            <Flex align="center" gap={4}>
              <Typography>
                국민은행 이체를 위해서는 국민은행 기업 OpenAPI 서비스 가입이 필요합니다.
              </Typography>
              <Button
                type="link"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onClick={handleClickKBGuide}
              >
                이용가이드
                <RiArrowRightUpLine size="16" color={colorTokens.iconPrimary} />
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex
        justify="space-between"
        style={{
          width: '100%',
        }}
        align="center"
      >
        <Typography>{`출금 가능 계좌 ${
          accountList.filter((acc) => acc.accountBalance > 0).length
        }개`}</Typography>
        <Flex align="center" gap={8}>
          <Typography
            style={{
              color: colorTokens.textDescription,
            }}
          >
            방금 전
          </Typography>
          <SyncOutlined
            onClick={() => {
              refetch();
            }}
            style={{
              cursor: 'pointer',
              color: colorTokens.iconDefault,
            }}
          />
        </Flex>
      </Flex>
      <Table
        loading={isFetching}
        columns={COLUMNS}
        rowSelection={{
          // 특정 column은 disable
          getCheckboxProps: (record) => ({
            disabled: record.accountBalance === 0,
          }),
          type: 'radio',
          ...rowSelection,
          selectedRowKeys: selectedAccountId ? [selectedAccountId] : [],
        }}
        style={{
          width: '100%',
        }}
        dataSource={accountList.map((item) => {
          return {
            ...item,
            key: item.accountId,
          };
        })}
        scroll={{ y: 'max(calc(100vh - 600px), 20vh)' }}
        pagination={false}
      />
      <WiringTypeSelector
        selectedBank={selectedBank}
        reservationInfo={reservationInfo}
        isPayrollMode={isPayrollMode}
        compressInfo={compressInfo}
        setCompressInfo={setCompressInfo}
        setIsPayrollMode={setIsPayrollMode}
        setReservationInfo={setReservationInfo}
      />
      <Flex justify="flex-end" gap={8}>
        <Button
          type="primary"
          disabled={buttonDisabled}
          onClick={async () => {
            const checkResult = await check({
              wiringBank: selectedBank,
            });

            if (!checkResult) return;

            if (!selectedAccountId || !selectedAccountNumber) return;

            props.onFinish({
              wiringBank: selectedBank,
              accountInfoId: selectedAccountId,
              accountNumber: selectedAccountNumber,
              isPayroll: isPayrollMode,
              depositInfo: props.model?.depositInfo || [],
              reservationInfo,
              compressInfo,
            });
          }}
        >
          다음
        </Button>
      </Flex>
    </Flex>
  );
};

export default WithdrawalSelectStep;
